"use client";

import { useState } from "react";
import { UploadProgressBar } from "@/components/UploadProgressBar";
import { useUploads, type UploadItem } from "@/components/upload/UploadProvider";
import {
  useMagazineUploads,
  type MagazineUploadItem,
} from "@/components/upload/MagazineUploadProvider";
import {
  useThumbnailJobs,
  type ThumbnailTrayItem,
} from "@/components/upload/ThumbnailJobProvider";
import { useTransferRate } from "@/lib/use-transfer-rate";
import {
  formatBytes,
  formatEta,
  formatEtaShort,
  formatSpeed,
} from "@/lib/upload-with-progress";

type TrayItem = {
  id: string;
  fileName: string;
  kind: "video" | "magazine" | "thumbnail";
  phase: UploadItem["phase"] | MagazineUploadItem["phase"] | ThumbnailTrayItem["phase"];
  loaded: number;
  total: number;
  errorMessage?: string;
  resourceId?: string;
  matchTitle?: string;
  matchVideoId?: string;
};

function toTrayItem(item: UploadItem): TrayItem {
  return {
    id: item.id,
    fileName: item.fileName,
    kind: "video",
    phase: item.phase,
    loaded: item.loaded,
    total: item.total,
    errorMessage: item.errorMessage,
    resourceId: item.videoId,
    matchTitle: item.matchTitle,
    matchVideoId: item.matchVideoId,
  };
}

function magazineToTrayItem(item: MagazineUploadItem): TrayItem {
  return {
    id: item.id,
    fileName: item.fileName,
    kind: "magazine",
    phase: item.phase,
    loaded: item.loaded,
    total: item.total,
    errorMessage: item.errorMessage,
    resourceId: item.magazineId,
  };
}

function thumbnailToTrayItem(item: ThumbnailTrayItem): TrayItem {
  return {
    id: item.videoId,
    fileName: item.title,
    kind: "thumbnail",
    phase: item.phase,
    loaded: item.loaded,
    total: item.total,
    errorMessage: item.error,
    resourceId: item.videoId,
  };
}

function reviewCount(uploads: TrayItem[]): number {
  return uploads.filter((u) => u.phase === "duplicate").length;
}

const IN_QUEUE = new Set([
  "queued",
  "hashing",
  "checking",
  "presigning",
  "uploading",
  "downloading",
  "extracting",
  "creating",
]);

function queueStats(uploads: TrayItem[]) {
  const queued = uploads.filter((u) => IN_QUEUE.has(u.phase));
  let bytesLeft = 0;
  for (const item of queued) {
    if (item.phase === "uploading" || item.phase === "downloading") {
      bytesLeft += Math.max(0, item.total - item.loaded);
    } else {
      bytesLeft += Math.max(0, item.total);
    }
  }
  return { fileCount: queued.length, bytesLeft };
}

function doneLinksFor(item: TrayItem, isAdmin: boolean): { href: string; label: string }[] {
  if (!item.resourceId) return [];
  if (item.kind === "magazine") {
    return [{ href: `/admin/magazines/${item.resourceId}/edit`, label: "Edit" }];
  }
  const links = [
    { href: `/videos/${item.resourceId}/poster`, label: "Choose screenshot" },
  ];
  if (isAdmin) {
    links.push({ href: `/admin/videos/${item.resourceId}/edit`, label: "Edit" });
  }
  return links;
}

export function UploadTray({ isAdmin = false }: { isAdmin?: boolean }) {
  const videoCtx = useUploads();
  const magazineCtx = useMagazineUploads();
  const thumbnailCtx = useThumbnailJobs();
  const [minimized, setMinimized] = useState(false);

  const uploads: TrayItem[] = [
    ...videoCtx.uploads.map(toTrayItem),
    ...magazineCtx.uploads.map(magazineToTrayItem),
    ...thumbnailCtx.jobs.map(thumbnailToTrayItem),
  ];

  const transferring = uploads.find(
    (u) => u.phase === "uploading" || u.phase === "downloading",
  );
  const bytesPerSec = useTransferRate(
    transferring?.loaded ?? 0,
    Boolean(transferring),
    transferring ? `${transferring.kind}-${transferring.id}` : undefined,
  );

  if (uploads.length === 0) return null;

  const hasUploads =
    videoCtx.uploads.length > 0 || magazineCtx.uploads.length > 0;
  const hasThumbnails = thumbnailCtx.jobs.length > 0;
  const trayTitle =
    hasUploads && hasThumbnails
      ? "Activity"
      : hasThumbnails
        ? "Thumbnails"
        : "Uploads";

  const review = reviewCount(uploads);
  const completedCount = uploads.filter(
    (u) => u.phase === "done" || u.phase === "error",
  ).length;
  const { fileCount: queueFiles, bytesLeft } = queueStats(uploads);
  const queueEtaSec =
    bytesPerSec && bytesPerSec > 0 && bytesLeft > 0
      ? bytesLeft / bytesPerSec
      : null;
  const fileEtaSec =
    bytesPerSec &&
    bytesPerSec > 0 &&
    transferring &&
    transferring.total > transferring.loaded
      ? (transferring.total - transferring.loaded) / bytesPerSec
      : null;

  const queueSummary = [
    queueFiles > 0
      ? `${queueFiles} ${queueFiles === 1 ? "file" : "files"}`
      : null,
    queueFiles > 0 ? `${formatBytes(bytesLeft)} left` : null,
    bytesPerSec != null && bytesPerSec > 0 ? formatSpeed(bytesPerSec) : null,
    queueEtaSec != null ? formatEta(queueEtaSec) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const minimizedSummary = [
    queueFiles > 0 ? `${queueFiles} left` : null,
    queueEtaSec != null ? formatEtaShort(queueEtaSec) : null,
  ]
    .filter(Boolean)
    .join(" · ");

  function cancelItem(item: TrayItem) {
    if (item.kind === "video") {
      videoCtx.cancelUpload(item.id);
    } else if (item.kind === "magazine") {
      magazineCtx.cancelUpload(item.id);
    }
  }

  function clearCompleted() {
    videoCtx.clearCompleted();
    magazineCtx.clearCompleted();
    thumbnailCtx.clearCompleted();
  }

  if (minimized) {
    return (
      <button
        type="button"
        onClick={() => setMinimized(false)}
        className="fixed bottom-4 right-4 z-50 rounded-full border border-border bg-surface px-4 py-2 text-sm font-medium text-foreground shadow-card transition hover:bg-muted"
      >
        {trayTitle}
        {minimizedSummary ? ` · ${minimizedSummary}` : ` (${uploads.length})`}
        {review > 0 ? ` · ${review} already in library` : ""}
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 flex w-[min(100vw-2rem,22rem)] flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-card">
      <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">{trayTitle}</p>
          {queueSummary ? (
            <p className="text-xs text-muted-foreground">{queueSummary}</p>
          ) : review > 0 ? (
            <p className="text-xs text-muted-foreground">
              {review} already in library
            </p>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {completedCount > 0 ? (
            <button
              type="button"
              onClick={clearCompleted}
              className="text-xs font-medium text-muted-foreground transition hover:text-foreground"
            >
              Clear completed
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => setMinimized(true)}
            className="text-xs font-medium text-muted-foreground transition hover:text-foreground"
            aria-label="Minimize upload tray"
          >
            −
          </button>
        </div>
      </div>

      <div className="max-h-80 space-y-2 overflow-y-auto p-3">
        {uploads.map((item) => (
          <UploadProgressBar
            key={`${item.kind}-${item.id}`}
            fileName={item.fileName}
            kind={item.kind}
            status={item.phase}
            loaded={item.loaded}
            total={item.total}
            bytesPerSec={
              (item.phase === "uploading" || item.phase === "downloading") &&
              bytesPerSec != null &&
              bytesPerSec > 0
                ? bytesPerSec
                : undefined
            }
            etaSeconds={
              (item.phase === "uploading" || item.phase === "downloading") &&
              fileEtaSec != null
                ? fileEtaSec
                : undefined
            }
            errorMessage={item.errorMessage}
            resourceId={item.resourceId}
            matchTitle={item.matchTitle}
            matchHref={
              item.kind === "video" && item.matchVideoId
                ? `/videos/${item.matchVideoId}`
                : undefined
            }
            doneLinks={doneLinksFor(item, isAdmin)}
            onCancel={
              item.kind !== "thumbnail" &&
              (item.phase === "queued" ||
                item.phase === "uploading" ||
                item.phase === "presigning" ||
                item.phase === "hashing" ||
                item.phase === "checking")
                ? () => cancelItem(item)
                : undefined
            }
            onSkipDuplicate={
              item.kind === "video" && item.phase === "duplicate"
                ? () => videoCtx.skipDuplicate(item.id)
                : undefined
            }
            onUploadAnyway={
              item.kind === "video" && item.phase === "duplicate"
                ? () => videoCtx.uploadAnyway(item.id)
                : undefined
            }
          />
        ))}
      </div>
    </div>
  );
}
